import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import CloseIcon from '../../images/CloseIcon';
import PlusIconWhite from '../../images/PlusIconWhite';
import EditIconV2 from '../../images/EditIconV2';
import DeleteIconV2 from '../../images/DeleteIconV2';
import { deletePromotion, getPromotionForDashboard, updateStatusPromotion } from '../../services/promotion.service';
import { showDeleteToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { getSchedules } from '../../services/schedule.service';
import { useResponsive } from '../../hook/useResponsive';
import PromotionIcon from '../../images/PromotionIcon';
import {
    Icon,
    ListColumn,
    ListFilter,
    ListQuery,
    ListRowAction,
    ListScreen,
    ListScreenHandle,
    cells,
} from '../../components/list';
import PromotionSwapSheet from '../../components/PromotionSwapSheet';

interface PromotionItemSummary {
    productName: string;
    value: number;
    discountType: number;
}

interface PromotionRow {
    id: string;
    promoName: string;
    promoDescription?: string | null;
    scheduleId?: string | null;
    isGlobal: boolean;
    isActive: boolean;
    promoType: number;
    itemsRotate: boolean;
    itemsCount: number;
    attributeCount: number;
    itemsSummary?: PromotionItemSummary[];
    /** Resolved client-side from GET /api/core/schedules (all). */
    scheduleName?: string;
}

const Promotion = () => {
    const preLanguage = localStorage.getItem('language');
    const { isMobile } = useResponsive();
    const [language, setLanguage] = useState<any>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const listRef = useRef<ListScreenHandle>(null);
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const [idDelete, setIdDelete] = useState<string>('');
    const [swapPromotionId, setSwapPromotionId] = useState<string>('');
    const inforTable: any = {
        img: <PromotionIcon />,
        title:
            language && language?.['No promos yet'] ? language?.['No promos yet'] : 'No promos yet',
        path: '/promotion/new',
        desc:
            language &&
                language?.['Create a promo to continue']
                ? language?.[
                'Create a promo to continue'
                ]
                : 'Create a promo to continue',
        titleCreate: language && language?.['Add promo'] ? language?.['Add promo'] : 'Add promo',
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
            event.preventDefault();
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);
    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);

    /**
     * One page of promotions. The search box maps onto `searchPromoName`; the
     * Schedule / Description text filters onto the endpoint's other two
     * per-column params. Schedule names are resolved from a second request
     * (page 0 / limit 0 = all), as before.
     */
    const fetchPromotions = async (query: ListQuery) => {
        webLoadingFalse();
        const response: any = await getPromotionForDashboard({
            page: query.page,
            limit: query.limit,
            searchPromoName: query.search,
            searchSchedule: query.filters.searchSchedule,
            searchDescription: query.filters.searchDescription,
        });
        const responseSchedule: any = await getSchedules({ page: 0, limit: 0, searchValue: '' });
        const schedules: any[] = responseSchedule?.data?.data ?? [];
        const rows: PromotionRow[] = (response.data.data ?? []).map((each: any) => ({
            ...each,
            scheduleName: schedules.find((val: any) => val.id === each?.scheduleId)?.scheduleName,
        }));
        return { rows, count: response.data.count ?? 0 };
    };

    const goToPromotion = (id: string) => webNavigate('/' + preLanguage + '/promotion/' + id);

    /** The switch shows and flips isActive — a real on/off for the promo. */
    const handleUpdateStatus = async (row: PromotionRow) => {
        try {
            await updateStatusPromotion(row.id, { isActive: !row.isActive });
        } catch (error: any) {
            console.log("🚀 ~ handleUpdateStatus ~ error:", error)
            showApiError(error, { language });
        } finally {
            listRef.current?.reload();
        }
    }

    const handleShowDelete = (id: string) => {
        setShowDelete(true);
        setIdDelete(id);
    };
    const handleDeleteEmployee = async () => {
        try {
            const response = await deletePromotion(idDelete);
            showDeleteToast(language?.['Promotion Deleted !'] ?? 'Promotion Deleted !');
        } catch (error) {
            console.error('Error loading terms:', error);
            showApiError(error, { language });
        } finally {
            listRef.current?.reload();
            setShowDelete(false);
        }
    };

    /* ---------------- List kit configuration ---------------- */

    /** Plain-language promo type: what actually happens on the till. */
    const typeLabel = (p: PromotionRow) => {
        if (p.promoType === 1) {
            return language?.['Automatic at till'] ?? 'Automatic at till';
        }
        if (p.promoType === 2) {
            return p.isGlobal
                ? (language?.['Staff applies · whole ticket'] ?? 'Staff applies · whole ticket')
                : (language?.['Staff applies at payment'] ?? 'Staff applies at payment');
        }
        return language?.['Promo'] ?? 'Promo';
    };

    /** "Espresso, Macchiato · 1.00 price override" from the list summary. */
    const itemsLabel = (p: PromotionRow) => {
        const summary = p.itemsSummary ?? [];
        if (summary.length === 0) {
            return p.attributeCount > 0
                ? `${p.attributeCount} ${language?.['attributes'] ?? 'attributes'}`
                : '';
        }
        const names = summary.map(s => s.productName).join(', ');
        const extra = p.itemsCount > summary.length ? ` +${p.itemsCount - summary.length}` : '';
        const first = summary[0];
        const price =
            first.discountType === 3
                ? ` · ${Number(first.value).toFixed(2)} ${language?.['price override'] ?? 'price override'}`
                : '';
        return `${names}${extra}${price}`;
    };

    const columns: ListColumn<PromotionRow>[] = [
        {
            key: 'promoName',
            label: 'Promo name',
            width: 260,
            hideable: false,
            render: cells.primary(p => p.promoName),
            mobile: 'title',
            onClick: p => goToPromotion(String(p.id)),
        },
        {
            key: 'typeLabel',
            label: 'Type',
            width: 180,
            render: cells.typePill(p => typeLabel(p)),
            value: p => typeLabel(p),
            mobile: 'sheet',
        },
        {
            key: 'scheduleName',
            label: 'Schedules',
            width: 180,
            value: p => p.scheduleName ?? '',
            mobile: 'subtitle',
        },
        {
            key: 'itemsSummary',
            label: 'Items',
            width: 240,
            value: p => itemsLabel(p),
            mobile: 'subtitle',
        },
        {
            key: 'itemsRotate',
            label: 'Items change daily',
            width: 130,
            value: p =>
                p.itemsRotate
                    ? (language?.['Yes'] ?? 'Yes')
                    : (language?.['No'] ?? 'No'),
            mobile: 'sheet',
        },
        {
            key: 'promoDescription',
            label: 'Description',
            width: 220,
            value: p => (p.promoDescription ? p.promoDescription : '-'),
            mobile: 'sheet',
        },
        {
            key: 'isActive',
            label: 'Active',
            width: 90,
            render: cells.toggle(p => !!p.isActive, handleUpdateStatus),
            mobile: 'trailing',
        },
    ];

    const filters: ListFilter[] = [
        { key: 'searchSchedule', label: 'Schedules', kind: 'text' },
        { key: 'searchDescription', label: 'Description', kind: 'text' },
    ];

    const rowActions: ListRowAction<PromotionRow>[] = [
        {
            key: 'swap',
            label: "Change today's items",
            icon: <Icon name="sync" color="#0F7679" />,
            sheetIcon: 'sync',
            sub: "Swap the dishes — price & schedule stay",
            hidden: p => !p.itemsRotate,
            onRun: p => setSwapPromotionId(String(p.id)),
        },
        {
            key: 'edit',
            label: 'Edit',
            icon: <EditIconV2 />,
            sheetIcon: 'inventory',
            onRun: p => goToPromotion(String(p.id)),
        },
        {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteIconV2 />,
            sheetIcon: 'close',
            danger: true,
            onRun: p => handleShowDelete(String(p.id)),
        },
    ];

    return (
        <>
            <PromotionSwapSheet
                open={!!swapPromotionId}
                promotionId={swapPromotionId}
                language={language}
                onClose={() => setSwapPromotionId('')}
                onSaved={() => listRef.current?.reload()}
            />
            <div
                className="h-full relative"
                style={{
                    overflowY: isMobile ? 'auto' : undefined,
                    paddingBottom: isMobile ? '24px' : undefined,
                    display: isMobile ? undefined : 'flex',
                    flexDirection: isMobile ? undefined : 'column',
                }}
            >
                <div className="lg:flex items-center justify-between">
                    <p
                        style={{ color: '#222525' }}
                        className="text-[24px] leading-[130%] font-medium font-inter"
                    >
                        {language && language?.['Promo'] ? language?.['Promo'] : 'Promo'}
                    </p>
                    <div
                        className="cursor-pointer flex items-center justify-center"
                        style={{
                            gap: '8px',
                            padding: "8px 24px 8px 16px",
                            backgroundColor: '#0F7679',
                            borderRadius: '30px',
                            width: 'fit-content',
                            marginTop: isMobile ? '20px' : '0px',
                        }}
                        onClick={() => {
                            webNavigate('/' + preLanguage + '/promotion/new');
                        }}
                    >
                        <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
                            <PlusIconWhite />
                        </div>
                        <p
                            style={{ color: '#FFFFFF' }}
                            className="text-[14px] leading-[130%] font-medium font-inter"
                        >
                            {language && language?.['Add promo'] ? language?.['Add promo'] : 'Add promo'}
                        </p>
                    </div>
                </div>
                <ListScreen
                    ref={listRef}
                    language={language}
                    columns={columns}
                    fetchRows={fetchPromotions}
                    searchPlaceholder="Search promos"
                    searchInputRef={searchInputRef}
                    filters={filters}
                    rowActions={rowActions}
                    countNoun="promos"
                    emptyState={inforTable}
                />
            </div>
            <div
                className={`fixed inset-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                    } transition-opacity duration-300 ease-in-out`}
                style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100vw", height: "100vh", left: 0, top: 0 }}
            >
                <div
                    className={`transform transition-transform duration-300 ease-in-out ${showDelete ? 'translate-y-0' : 'translate-y-4 opacity-0'
                        }`}
                    style={{
                        padding: '24px',
                        borderRadius: '24px',
                        backgroundColor: '#FFFFFF',
                        width: '530px',
                        maxWidth: 'calc(100vw - 32px)',
                        transition: 'transform 0.3s ease, opacity 0.3s ease',
                    }}
                >
                    <div className="flex items-center justify-between">
                        <p
                            style={{ fontSize: '20px', color: '#222525' }}
                            className="font-semibold font-inter leading-[24px]"
                        >
                            {language && language?.['Delete promotion?']
                                ? language?.['Delete promotion?']
                                : 'Delete promotion?'}
                        </p>
                        <div
                            onClick={() => setShowDelete(false)}
                            className="flex items-center justify-center cursor-pointer"
                            style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '100%',
                            }}
                        >
                            <CloseIcon />
                        </div>
                    </div>
                    <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
                        <p
                            className="text-[14px] font-inter leading-[24px]"
                            style={{ color: '#6A7878' }}
                        >
                            {language &&
                                language?.[
                                'This promotion will be permanently deleted. Are you sure you want to continue'
                                ]
                                ? language?.[
                                'This promotion will be permanently deleted. Are you sure you want to continue'
                                ]
                                : 'This promotion will be permanently deleted. Are you sure you want to continue'}
                        </p>
                    </div>
                    <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
                        <div
                            onClick={() => setShowDelete(false)}
                            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                            style={{
                                borderRadius: '30px',
                                padding: '10px 24px',
                                border: '1px solid #C9D7D8',
                                color: '#222525',
                            }}
                        >
                            {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                        </div>
                        <div
                            onClick={handleDeleteEmployee}
                            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                            style={{
                                borderRadius: '30px',
                                padding: '10px 24px',
                                backgroundColor: '#BA0E13',
                                color: '#FFFFFF',
                            }}
                        >
                            {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Promotion;
